import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import logger from '../lib/logger';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import api, { bookmarkAPI } from '../lib/api';
import { renderLatex, formatTime } from '../lib/renderLatex';
import { 
  Clock, Check, X, ChevronRight, ChevronLeft, RotateCcw, Target, 
  Zap, Trophy, BookOpen, ArrowRight, Sparkles, Heart, Star, Flame, 
  Filter, FileText, Lightbulb, GitMerge, ArrowLeft, CheckCircle,
  XCircle, AlertCircle, Bookmark as BookmarkIcon, Share2, MessageSquare, Hash, Lock, Calendar
} from 'lucide-react';

import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner, { ButtonSpinner } from '../components/LoadingSpinner';

// Separate Timer component to avoid re-rendering the parent on every tick
const QuestionTimer = React.memo(({ isActive, initialTime, onTimeUpdate }) => {
  const [time, setTime] = useState(initialTime);
  const timeRef = useRef(time);
  
  useEffect(() => {
    setTime(initialTime);
    timeRef.current = initialTime;
  }, [initialTime]);
  
  useEffect(() => {
    if (!isActive) return;
    
    const interval = setInterval(() => {
      timeRef.current += 1;
      setTime(timeRef.current);
      if (onTimeUpdate) onTimeUpdate(timeRef.current);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isActive, onTimeUpdate]);
  
  return (
    <div className="text-2xl font-bold font-mono text-purple-600">
      {formatTime(time)}
    </div>
  );
});

const PracticeNew = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Navigation states
  const [currentScreen, setCurrentScreen] = useState('exam'); // exam -> mode -> subject -> chapter-or-whole -> practice
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  
  // Selection states
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null); // 'pyq', 'important', 'mixed'
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [practiceType, setPracticeType] = useState(null); // 'whole' or 'chapters'
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]); // For PYQ year filtering
  const [availableYears, setAvailableYears] = useState([]); // Years available for selected exam
  
  // Practice states
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userAnswers, setUserAnswers] = useState({}); // Store all answers
  const [stats, setStats] = useState({ correct: 0, wrong: 0, skipped: 0, streak: 0 });
  const [questionTimers, setQuestionTimers] = useState({}); // Time for each question
  const [loading, setLoading] = useState(false);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false); // For endless practice
   const [bookmarked, setBookmarked] = useState(new Set());
  
  const toggleBookmark = async () => {
    const qId = questions[currentIndex]?.id;
    if (!qId) return;
    if (bookmarked.has(qId)) {
      await bookmarkAPI.remove(qId);
      setBookmarked(prev => {
        const next = new Set(prev);
        next.delete(qId);
        return next;
      });
      toast.info('Bookmark removed');
    } else {
      await bookmarkAPI.add(qId);
      setBookmarked(prev => new Set(prev).add(qId));
      toast.success('Bookmarked!');
    }
  };
  const [chapters, setChapters] = useState([]);
  const [selectedChapterName, setSelectedChapterName] = useState(null); // Track chapter name for AI generation
  const [shownQuestionIds, setShownQuestionIds] = useState([]); // Track shown questions for endless mode
  const [integerAnswer, setIntegerAnswer] = useState(''); // For integer/numerical type questions
  
  // Ref for numerical input to maintain focus
  const numericalInputRef = useRef(null);
  
  // Timer ref to store live time without causing re-renders
  const timerRef = useRef(0);
  const [timerKey, setTimerKey] = useState(0); // Key to reset timer component

  // Callback to track time updates (stored in ref, not state)
  const handleTimeUpdate = useCallback((time) => {
    timerRef.current = time;
  }, []);

  // Reset timer when moving to a new question
  useEffect(() => {
    if (!userAnswers[currentIndex]) {
      timerRef.current = questionTimers[currentIndex] || 0;
      setTimerKey(prev => prev + 1); // Force timer component reset
    }
  }, [currentIndex, questionTimers, userAnswers]);

  // Keep numerical input focused when typing
  useLayoutEffect(() => {
    if (currentScreen === 'practice' && numericalInputRef.current) {
      // Check if this is a numerical question
      const currentQuestion = questions[currentIndex];
      const isIntegerType = currentQuestion?.question_type?.toLowerCase() === 'integer' || 
                            currentQuestion?.question_type?.toLowerCase() === 'numerical' ||
                            (!currentQuestion?.options || currentQuestion?.options?.length === 0);
      
      if (isIntegerType && !userAnswers[currentIndex]) {
        numericalInputRef.current.focus();
      }
    }
  });

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      const response = await api.get('/exams');
      setExams(response.data);
      if (examId) {
        const exam = response.data.find(e => e.id === examId);
        if (exam) {
          setSelectedExam(exam);
          setCurrentScreen('mode');
        }
      }
    } catch (error) {
      toast.error('Failed to load exams');
    }
  };

  const loadSubjects = async (exam) => {
    setLoadingSubjects(true);
    try {
      const response = await api.get(`/exams/${exam.id}/subjects`);
      setSubjects(response.data);
      if (response.data.length === 0) {
        toast.warning('No subjects available for this exam');
      }
    } catch (error) {
      logger.error('Failed to load subjects:', error);
      toast.error('Failed to load subjects');
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const loadChapters = async (examId, subjectId) => {
    setLoadingChapters(true);
    try {
      // Get chapters from API using subject_id
      const response = await api.get(`/subjects/${subjectId}/chapters`);
      
      if (response.data && response.data.length > 0) {
        setChapters(response.data);
      } else {
        // Fallback: show message that no chapters found
        toast.info('No chapters found for this subject');
        setChapters([]);
      }
    } catch (error) {
      logger.error('Failed to load chapters:', error);
      toast.error('Failed to load chapters');
      setChapters([]);
    } finally {
      setLoadingChapters(false);
    }
  };

  const loadQuestions = async (mode, subject, selectedChaptersList = []) => {
    setLoading(true);
    try {
      // Store chapter name for AI generation later
      if (selectedChaptersList.length > 0) {
        const chapterObj = chapters.find(c => selectedChaptersList.includes(c.id));
        setSelectedChapterName(chapterObj?.name || null);
      } else {
        setSelectedChapterName(null);
      }

      let params = { 
        exam_id: selectedExam.id,
        subject: subject,
        limit: 1000
      };

      if (mode === 'pyq') {
        params.is_pyq = true;
        // Add year filtering for PYQ mode
        if (selectedYears.length > 0) {
          params.years = selectedYears.join(',');
        }
      }
      // 'important' and 'mixed' get all questions

      if (selectedChaptersList.length > 0) {
        params.chapters = selectedChaptersList.join(',');
      }

      const response = await api.get('/questions', { params });
      // API returns {total, questions:[...]} – extract array safely
      const unwrap = (d) => Array.isArray(d) ? d : (d?.questions ?? []);

      let finalQuestions = unwrap(response.data);

      // If a mode filter returned nothing, fall back to all questions for the subject
      if (finalQuestions.length === 0 && (mode === 'pyq' || mode === 'important')) {
        const fallbackParams = {
          exam_id: selectedExam.id,
          subject: subject,
          limit: 1000,
          ...(selectedChaptersList.length > 0 ? { chapters: selectedChaptersList.join(',') } : {})
        };
        const fallback = await api.get('/questions', { params: fallbackParams });
        const fallbackQuestions = unwrap(fallback.data);
        if (fallbackQuestions.length > 0) {
          toast.info(`No ${mode === 'pyq' ? 'PYQ-tagged' : 'important-tagged'} questions yet – showing all questions instead.`);
          finalQuestions = fallbackQuestions;
        }
      }

      // If no questions found at all, show message to admin
      if (finalQuestions.length === 0) {
        toast.error('No questions available for this selection. Please add questions in Admin Panel.');
        setCurrentScreen('subject');
        return;
      }
      
      const shuffled = finalQuestions.sort(() => Math.random() - 0.5);
      setQuestions(shuffled);
      setShownQuestionIds(shuffled.map(q => q.id));
      setStats({ correct: 0, wrong: 0, skipped: 0, streak: 0 });
      setCurrentIndex(0);
      setUserAnswers({});
      setCurrentScreen('practice');
      
      // Update streak — practicing counts as daily activity
      try { await api.post('/streak/update'); } catch {}
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  // Load more questions from database (no AI generation)
  const loadMoreQuestions = async (isInitial = false) => {
    if (loadingMore) return;
    
    setLoadingMore(true);
    try {
      // Get more questions from database excluding already shown
      const params = {
        exam_id: selectedExam.id,
        subject: selectedSubject,
        limit: 20
      };
      
      if (selectedChapters.length > 0) {
        params.chapters = selectedChapters.join(',');
      }
      
      const response = await api.get('/questions', { params });
      const allLoaded = Array.isArray(response.data) ? response.data : (response.data?.questions ?? []);

      // Filter out already shown questions
      const newQuestions = allLoaded.filter(q => !shownQuestionIds.includes(q.id));

      if (newQuestions.length > 0) {
        const shuffled = newQuestions.sort(() => Math.random() - 0.5).slice(0, 10);
        
        if (isInitial) {
          setQuestions(shuffled);
          setShownQuestionIds(shuffled.map(q => q.id));
          setStats({ correct: 0, wrong: 0, skipped: 0, streak: 0 });
          setCurrentIndex(0);
          setUserAnswers({});
          setCurrentScreen('practice');
        } else {
          setQuestions(prev => [...prev, ...shuffled]);
          setShownQuestionIds(prev => [...prev, ...shuffled.map(q => q.id)]);
        }
      } else if (isInitial) {
        toast.error('No questions available. Please add questions in Admin Panel.');
        setCurrentScreen('subject');
      } else {
        toast.info('You\'ve practiced all available questions.');
      }
    } catch (error) {
      logger.error('Failed to load more questions:', error);
      if (isInitial) {
        toast.error('Failed to load questions');
        setCurrentScreen('subject');
      }
    } finally {
      setLoadingMore(false);
    }
  };

  // ===== SCREEN 1: EXAM SELECTION =====
  const ExamSelectionScreen = () => {
    // Separate available and coming soon exams
    const availableExams = exams.filter(exam => exam.is_available);
    const comingSoonExams = exams.filter(exam => !exam.is_available);
    
    return (
    <div>
      <div className="mb-12 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-3">Choose Exam</h1>
        <p className="text-lg text-muted-foreground">Select which exam you want to practice</p>
      </div>

      {/* Available Exams */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
        {availableExams.map(exam => (
          <Card
            key={exam.id}
            className="p-8 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-2 border-blue-200 dark:border-blue-800 hover:border-blue-400 hover:shadow-xl transition-all cursor-pointer group"
            onClick={() => {
              setSelectedExam(exam);
              loadSubjects(exam);
              setCurrentScreen('mode');
            }}
          >
            <div className="text-6xl mb-4">{exam.icon}</div>
            <h3 className="text-2xl font-bold text-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
              {exam.name}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">{exam.category}</p>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30">Available</Badge>
              <ArrowRight className="text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition-transform ml-auto" />
            </div>
          </Card>
        ))}
      </div>

      {/* Coming Soon Exams */}
      {comingSoonExams.length > 0 && (
        <>
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-muted-foreground">Coming Soon</h2>
            <p className="text-sm text-muted-foreground">More exams will be available soon</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {comingSoonExams.map(exam => (
              <Card
                key={exam.id}
                className="p-4 bg-muted/30 border border-border opacity-60 cursor-not-allowed relative overflow-hidden"
              >
                <div className="absolute top-2 right-2">
                  <Lock size={16} className="text-muted-foreground" />
                </div>
                <div className="text-3xl mb-2 grayscale">{exam.icon}</div>
                <h3 className="text-sm font-semibold text-muted-foreground">
                  {exam.name}
                </h3>
                <Badge variant="outline" className="mt-2 text-xs">Coming Soon</Badge>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  )};

  // ===== SCREEN 2: MODE SELECTION (PYQ, Important, Mixed) =====
  const ModeSelectionScreen = () => (
    <div>
      <button
        onClick={() => setCurrentScreen('exam')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 group"
      >
        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
        <span>Back to Exam Selection</span>
      </button>

      <div className="mb-12 text-center">
        <div className="text-6xl mb-4">{selectedExam?.icon}</div>
        <h1 className="text-4xl font-bold text-foreground mb-2">{selectedExam?.name}</h1>
        <p className="text-lg text-muted-foreground">Choose a practice type</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {/* PYQ Card */}
        <Card
          className="p-8 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-2 border-purple-200 dark:border-purple-800 hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => {
            setSelectedMode('pyq');
            loadSubjects(selectedExam);
            setCurrentScreen('subject');
          }}
        >
          <FileText className="text-purple-600 dark:text-purple-400 mb-4" size={48} />
          <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
            Previous Year Questions
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Practice with official past exam papers from previous years
          </p>
          <Badge className="bg-purple-600 dark:bg-purple-500">Official PYQs</Badge>
        </Card>

        {/* Important Sources Card */}
        <Card
          className="p-8 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-2 border-orange-200 dark:border-orange-800 hover:border-orange-400 hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => {
            setSelectedMode('important');
            loadSubjects(selectedExam);
            setCurrentScreen('subject');
          }}
        >
          <Lightbulb className="text-orange-600 dark:text-orange-400 mb-4" size={48} />
          <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
            Important Sources
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Curated questions from important sources and high-frequency topics
          </p>
          <Badge className="bg-orange-600 dark:bg-orange-500">High Value</Badge>
        </Card>

        {/* Mixed Card */}
        <Card
          className="p-8 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-2 border-green-200 dark:border-green-800 hover:border-green-400 hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => {
            setSelectedMode('mixed');
            loadSubjects(selectedExam);
            setCurrentScreen('subject');
          }}
        >
          <GitMerge className="text-green-600 dark:text-green-400 mb-4" size={48} />
          <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
            Mixed
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Combination of both PYQs and important source questions
          </p>
          <Badge className="bg-green-600 dark:bg-green-500">Complete Mix</Badge>
        </Card>
      </div>
    </div>
  );

  // ===== SCREEN 3: SUBJECT SELECTION =====
  const SubjectSelectionScreen = () => {
    // Generate list of years (2015-2026, or current year + 10 years back)
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 12 }, (_, i) => currentYear - i);
    
    const toggleYear = (year) => {
      setSelectedYears(prev => 
        prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
      );
    };
    
    const toggleAllYears = () => {
      if (selectedYears.length === years.length) {
        setSelectedYears([]);
      } else {
        setSelectedYears(years);
      }
    };
    
    return (
    <div>
      <button
        onClick={() => setCurrentScreen('mode')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 group"
      >
        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
        <span>Back to Mode Selection</span>
      </button>

      {/* Year Filter for PYQ Mode */}
      {selectedMode === 'pyq' && (
        <Card className="mb-8 p-6 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-2 border-purple-200 dark:border-purple-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="text-purple-600 dark:text-purple-400" size={24} />
              <h3 className="text-lg font-bold text-foreground">Filter by Year</h3>
            </div>
            <Button
              onClick={toggleAllYears}
              variant="outline"
              size="sm"
              className="text-xs"
            >
              {selectedYears.length === years.length ? 'Clear All' : 'Select All'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {selectedYears.length === 0 
              ? 'Select specific years to practice, or leave empty to practice all years' 
              : `${selectedYears.length} year${selectedYears.length > 1 ? 's' : ''} selected`}
          </p>
          <div className="flex flex-wrap gap-2">
            {years.map(year => (
              <Badge
                key={year}
                className={`cursor-pointer px-4 py-2 text-sm transition-all ${
                  selectedYears.includes(year)
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
                onClick={() => toggleYear(year)}
              >
                {year}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <div className="mb-12 text-center">
        <h1 className="text-4xl font-bold text-foreground mb-2">Select Subject</h1>
        <p className="text-lg text-muted-foreground">
          Choose {selectedMode === 'pyq' ? 'Previous Year Questions' : selectedMode === 'important' ? 'Important Sources' : 'Mixed'} for a subject
        </p>
      </div>

      {loadingSubjects ? (
        <LoadingSpinner text="Loading subjects..." />
      ) : subjects.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg border-2 border-dashed border-border">
          <AlertCircle className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-muted-foreground">No subjects available for this exam</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {subjects.map(subject => (
            <Card
              key={subject.id}
              className="p-6 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 hover:border-primary hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => {
                setSelectedSubject(subject.name);
                loadChapters(selectedExam.id, subject.id);
                setCurrentScreen('practice-type');
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors capitalize">
                  {subject.name}
                </h3>
                <BookOpen className="text-muted-foreground group-hover:text-primary transition-colors" size={24} />
              </div>
              <p className="text-sm text-muted-foreground">
                Practice all {subject.name} questions
              </p>
              <ArrowRight className="text-primary mt-4 group-hover:translate-x-1 transition-transform" size={20} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
  };

  // ===== SCREEN 4: PRACTICE TYPE (Whole Subject or By Chapters) =====
  const PracticeTypeScreen = () => (
    <div>
      <button
        onClick={() => setCurrentScreen('subject')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 group"
      >
        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
        <span>Back to Subject Selection</span>
      </button>

      <div className="mb-12 text-center">
        <h1 className="text-4xl font-bold text-foreground mb-2 capitalize">
          {selectedSubject}
        </h1>
        <p className="text-lg text-muted-foreground">How would you like to practice?</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
        {/* Whole Subject */}
        <Card
          className="p-8 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-2 border-blue-200 dark:border-blue-800 hover:border-blue-400 hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => {
            setPracticeType('whole');
            loadQuestions(selectedMode, selectedSubject, []);
          }}
        >
          <BookOpen className="text-blue-600 dark:text-blue-400 mb-4" size={48} />
          <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            Entire Subject
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Practice all questions from the complete subject
          </p>
          <Badge className="bg-blue-600 dark:bg-blue-500">Comprehensive</Badge>
        </Card>

        {/* By Chapters */}
        <Card
          className="p-8 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-2 border-indigo-200 dark:border-indigo-800 hover:border-indigo-400 hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => {
            setPracticeType('chapters');
            setCurrentScreen('chapter-selection');
          }}
        >
          <Filter className="text-indigo-600 dark:text-indigo-400 mb-4" size={48} />
          <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
            By Chapters
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Select specific chapters to practice
          </p>
          <Badge className="bg-indigo-600 dark:bg-indigo-500">Targeted</Badge>
        </Card>
      </div>
    </div>
  );

  // ===== SCREEN 5: CHAPTER SELECTION =====
  const ChapterSelectionScreen = () => (
    <div>
      <button
        onClick={() => setCurrentScreen('practice-type')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 group"
      >
        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
        <span>Back</span>
      </button>

      <div className="mb-12 text-center">
        <h1 className="text-4xl font-bold text-foreground mb-2 capitalize">
          Select Chapters - {selectedSubject}
        </h1>
        <p className="text-lg text-muted-foreground">
          Choose which chapters to practice ({selectedChapters.length} selected)
        </p>
      </div>

      {loadingChapters ? (
        <div className="max-w-3xl mx-auto text-center py-12">
          <p className="text-muted-foreground">Loading chapters...</p>
        </div>
      ) : chapters.length === 0 ? (
        <div className="max-w-3xl mx-auto text-center py-12">
          <p className="text-muted-foreground mb-4">No chapters available for this subject yet.</p>
          <Button onClick={() => setCurrentScreen('practice-type')} variant="outline">
            Try a Different Subject
          </Button>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {chapters.map(chapter => (
              <Card
                key={chapter.id}
                className={`p-6 border-2 cursor-pointer transition-all ${
                  selectedChapters.includes(chapter.id)
                    ? 'border-primary bg-primary/10 dark:bg-primary/20'
                    : 'border-gray-200 dark:border-gray-800 hover:border-primary'
                }`}
                onClick={() => {
                  setSelectedChapters(prev =>
                    prev.includes(chapter.id)
                      ? prev.filter(id => id !== chapter.id)
                      : [...prev, chapter.id]
                  );
                }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{chapter.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {chapter.category && `Category: ${chapter.category}`}
                    </p>
                  </div>
                  {selectedChapters.includes(chapter.id) && (
                    <CheckCircle className="text-primary" size={24} />
                  )}
                </div>
              </Card>
            ))}
          </div>

          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => setSelectedChapters([])}
              className="flex-1"
            >
              Clear All
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedChapters(chapters.map(c => c.id))}
              className="flex-1"
            >
              Select All
            </Button>
            <Button
              onClick={() => {
                if (selectedChapters.length === 0) {
                  toast.error('Please select at least one chapter');
                  return;
                }
                loadQuestions(selectedMode, selectedSubject, selectedChapters);
              }}
              className="flex-1 bg-primary"
              disabled={selectedChapters.length === 0}
            >
              Start Practice ({selectedChapters.length})
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // ===== SCREEN 6: PRACTICE SCREEN =====
  const PracticeScreen = () => {
    // Safety check: if no questions, go back to subject selection
    if (questions.length === 0) {
      setTimeout(() => {
        toast.error('No questions loaded. Please select a subject.');
        setCurrentScreen('subject');
      }, 0);
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No questions available...</p>
        </div>
      );
    }
    
    const currentQuestion = questions[currentIndex];
    const answeredCount = Object.keys(userAnswers).length;
    const progress = (answeredCount / questions.length) * 100;
    const isAnswered = userAnswers[currentIndex] !== undefined;
    
    // Calculate isCorrect for both MCQ and Integer types
    const isIntegerType = currentQuestion?.question_type?.toLowerCase() === 'integer' || 
                          currentQuestion?.question_type?.toLowerCase() === 'numerical' ||
                          (!currentQuestion?.options || currentQuestion?.options?.length === 0);
    
    let isCorrect = false;
    if (isAnswered) {
      if (isIntegerType) {
        // For integer questions, compare numerically with tolerance
        const userAnswer = userAnswers[currentIndex]?.toString().trim();
        const correctAnswer = currentQuestion?.correct_answer?.toString().trim();
        try {
          const userNum = parseFloat(userAnswer);
          const correctNum = parseFloat(correctAnswer);
          isCorrect = !isNaN(userNum) && !isNaN(correctNum) && Math.abs(userNum - correctNum) < 0.001;
        } catch {
          isCorrect = userAnswer === correctAnswer;
        }
        if (!isCorrect) {
          isCorrect = userAnswer === correctAnswer;
        }
      } else {
        // For MCQ, direct comparison
        isCorrect = userAnswers[currentIndex] === currentQuestion?.correct_answer;
      }
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-8">
        {/* Header */}
        <div className="max-w-5xl mx-auto px-4 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {/* ExamDex Branding */}
              <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md overflow-hidden">
                <img src="/logo192.png" alt="ExamDex" className="w-full h-full object-cover" />
              </div>
              <button
                onClick={handleEndPractice}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
              >
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                <span>End Session</span>
              </button>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground capitalize">
                {selectedMode} | {selectedSubject}
                {selectedChapterName && ` | ${selectedChapterName}`}
              </p>
              <p className="text-xs text-primary font-medium">♾️ Endless Practice Mode</p>
            </div>
            <div className="flex items-center gap-2">
              {loadingMore && <ButtonSpinner size={16} />}
              <div className="text-sm font-semibold text-foreground">
                Q{currentIndex + 1} • {stats.correct + stats.wrong} done
              </div>
            </div>
          </div>

          {/* Progress Bar - Shows answered questions */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Questions Attempted</span>
              <span className="text-sm font-semibold text-foreground">{answeredCount} answered</span>
            </div>
            <Progress value={questions.length > 0 ? (answeredCount / questions.length) * 100 : 0} className="h-3" />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-5 gap-3">
            <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-xs text-muted-foreground">Correct</span>
              </div>
              <div className="text-2xl font-bold text-green-600">{stats.correct}</div>
            </Card>
            <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2 mb-1">
                <XCircle size={16} className="text-red-600" />
                <span className="text-xs text-muted-foreground">Wrong</span>
              </div>
              <div className="text-2xl font-bold text-red-600">{stats.wrong}</div>
            </Card>
            <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={16} className="text-yellow-600" />
                <span className="text-xs text-muted-foreground">Skipped</span>
              </div>
              <div className="text-2xl font-bold text-yellow-600">{stats.skipped}</div>
            </Card>
            <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-1">
                <Flame size={16} className="text-blue-600" />
                <span className="text-xs text-muted-foreground">Streak</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">{stats.streak}</div>
            </Card>
            <Card className={`p-4 border-purple-200 dark:border-purple-800 ${isAnswered ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-purple-100 dark:bg-purple-900/30'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-purple-600" />
                <span className="text-xs text-muted-foreground">Time</span>
              </div>
              {isAnswered ? (
                <div className="text-2xl font-bold font-mono text-purple-600">
                  {formatTime(questionTimers[currentIndex] || 0)}
                </div>
              ) : (
                <QuestionTimer
                  key={`timer-${currentIndex}-${timerKey}`}
                  isActive={currentScreen === 'practice' && !isAnswered}
                  initialTime={questionTimers[currentIndex] || 0}
                  onTimeUpdate={handleTimeUpdate}
                />
              )}
            </Card>
          </div>
        </div>

        {/* Question Card */}
        <div className="max-w-5xl mx-auto px-4">
          <Card className="p-8 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 shadow-lg mb-8">
            {/* Question Header */}
            <div className="mb-8 pb-8 border-b-2 border-gray-200 dark:border-gray-800">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex gap-2 mb-3">
                    {currentQuestion?.is_pyq && (
                      <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                        PYQ {currentQuestion?.year || ''}
                      </Badge>
                    )}
                    {isIntegerType ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
                        <Hash size={12} className="mr-1" />
                        Numerical
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">
                        MCQ
                      </Badge>
                    )}
                  </div>
                  <div 
                    className="text-xl md:text-2xl font-medium text-foreground leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: renderLatex(currentQuestion?.question_text || '') }}
                  />
                  {/* Diagram / question image */}
                  {currentQuestion?.image_url && (
                    <div className="mt-4" id={`qimg-${currentQuestion?.id}`}>
                      <img
                        src={currentQuestion.image_url}
                        alt=""
                        className="max-w-full rounded-lg border border-gray-200 dark:border-gray-700"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const wrapper = document.getElementById(`qimg-${currentQuestion?.id}`);
                          if (wrapper) wrapper.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
                {isAnswered && (
                  <div className={`text-4xl ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                    {isCorrect ? <CheckCircle /> : <XCircle />}
                  </div>
                )}
                {/* Bookmark Button */}
                <div className="flex gap-2">
                  <Button
                    onClick={toggleBookmark}
                    variant="ghost"
                    className={`rounded-xl border ${bookmarked.has(currentQuestion?.id) ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' : 'text-muted-foreground border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                    title="Bookmark question"
                  >
                    <BookmarkIcon size={20} fill={bookmarked.has(currentQuestion?.id) ? 'currentColor' : 'none'} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Options - MCQ or Integer Input */}
            <div className="space-y-4 mb-8">
              {/* Check if this is an integer/numerical type question */}
              {(currentQuestion?.question_type?.toLowerCase() === 'integer' || 
                currentQuestion?.question_type?.toLowerCase() === 'numerical' ||
                (!currentQuestion?.options || currentQuestion?.options?.length === 0)) ? (
                // Integer/Numerical Input
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Hash size={20} className="text-primary" />
                    <span className="text-sm font-medium text-muted-foreground">Enter your numerical answer</span>
                  </div>
                  <div className="flex gap-3">
                    <input
                      ref={numericalInputRef}
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      autoFocus
                      placeholder="Enter answer (e.g., 42, -3.14, 0.5)"
                      value={integerAnswer}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
                          setIntegerAnswer(val);
                        }
                      }}
                      disabled={isAnswered}
                      className={`flex-1 text-xl font-mono h-14 px-4 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary ${
                        isAnswered 
                          ? isCorrect 
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                            : 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-input'
                      }`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && integerAnswer.trim() && !isAnswered) {
                          handleIntegerSubmit();
                        }
                      }}
                    />
                    <Button
                      onClick={handleIntegerSubmit}
                      disabled={!integerAnswer.trim() || isAnswered}
                      className="h-14 px-8"
                    >
                      Submit
                    </Button>
                  </div>
                  {isAnswered && (
                    <div className={`p-4 rounded-lg ${isCorrect ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                      <p className="font-medium">
                        {isCorrect ? (
                          <span className="text-green-600">✓ Correct</span>
                        ) : (
                          <span className="text-red-600">
                            ✗ Wrong! Your answer: {userAnswers[currentIndex]} | Correct answer: {currentQuestion?.correct_answer}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                // MCQ Options
                currentQuestion?.options?.map((option, idx) => {
                  const optionLetter = String.fromCharCode(65 + idx);
                  const isSelected = userAnswers[currentIndex] === optionLetter;
                  const isCorrectOption = optionLetter === currentQuestion.correct_answer;
                  const showResult = isAnswered;

                  let optionClass = 'border-2 border-gray-200 dark:border-gray-800 hover:border-primary';
                  if (showResult) {
                    if (isCorrectOption) {
                      optionClass = 'border-2 border-green-500 bg-green-50 dark:bg-green-900/20';
                    } else if (isSelected && !isCorrect) {
                      optionClass = 'border-2 border-red-500 bg-red-50 dark:bg-red-900/20';
                    }
                  } else if (isSelected) {
                    optionClass = 'border-2 border-primary bg-primary/10 dark:bg-primary/20';
                  }

                  return (
                    <div
                      key={idx}
                      onClick={async () => {
                        if (!isAnswered) {
                          // Save the time spent on this question
                          setQuestionTimers(prev => ({
                            ...prev,
                            [currentIndex]: timerRef.current
                          }));
                          
                          setUserAnswers(prev => ({
                            ...prev,
                            [currentIndex]: optionLetter
                          }));
                          
                          // Check if correct
                          const isCorrectAnswer = optionLetter === currentQuestion.correct_answer;
                          
                          // Update stats
                          if (isCorrectAnswer) {
                            setStats(prev => ({
                              ...prev,
                              correct: prev.correct + 1,
                              streak: prev.streak + 1
                            }));
                            toast.success('Correct');
                          } else {
                            setStats(prev => ({
                              ...prev,
                              wrong: prev.wrong + 1,
                              streak: 0
                            }));
                            toast.error('❌ Wrong answer');
                            // Save mistake to database
                            try {
                              await api.post('/mistakes/save', { question_id: currentQuestion.id });
                            } catch (error) {
                              logger.error('Failed to save mistake:', error);
                            }
                          }
                          
                          // Save response for analytics
                          try {
                            await api.post('/practice/save-response', {
                              question_id: currentQuestion.id,
                              selected_answer: optionLetter,
                              is_correct: isCorrectAnswer,
                              time_spent_seconds: timerRef.current
                            });
                          } catch (error) {
                            logger.error('Failed to save practice response:', error);
                          }
                        }
                      }}
                      className={`p-6 rounded-lg cursor-pointer transition-all ${optionClass} ${
                        !isAnswered ? 'hover:bg-gray-50 dark:hover:bg-gray-800' : ''
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full font-bold flex items-center justify-center border-2 ${
                          isSelected || showResult ? (isCorrectOption ? 'bg-green-600 border-green-600 text-white' : isSelected ? 'bg-red-600 border-red-600 text-white' : 'border-gray-300 dark:border-gray-600') : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {optionLetter}
                        </div>
                        <div className="flex-1">
                          <div 
                            className="text-foreground font-medium"
                            dangerouslySetInnerHTML={{ __html: renderLatex(option.text || '') }}
                          />
                        </div>
                        {showResult && isCorrectOption && <CheckCircle className="text-green-600 flex-shrink-0" size={24} />}
                        {showResult && isSelected && !isCorrect && <XCircle className="text-red-600 flex-shrink-0" size={24} />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Explanation */}
            {isAnswered && currentQuestion?.explanation && (
              <div className={`p-6 rounded-xl mb-8 ${isCorrect ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-600' : 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600'}`}>
                <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                  <Lightbulb size={18} className={isCorrect ? 'text-green-600' : 'text-blue-600'} />
                  Explanation
                </h3>
                <div 
                  className="text-foreground leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderLatex(currentQuestion?.explanation || '') }}
                />
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  if (currentIndex > 0) {
                    setCurrentIndex(currentIndex - 1);
                  }
                }}
                disabled={currentIndex === 0}
                className="flex-1"
              >
                <ChevronLeft size={20} />
                Previous
              </Button>

              {!isAnswered && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setStats(prev => ({
                      ...prev,
                      skipped: prev.skipped + 1
                    }));
                    handleNext();
                  }}
                  className="flex-1"
                >
                  <ChevronRight size={20} />
                  Skip
                </Button>
              )}

              <Button
                onClick={handleNext}
                className="flex-1 bg-primary"
                disabled={!isAnswered}
              >
                {currentIndex === questions.length - 1 ? 'Finish' : 'Next'}
                <ChevronRight size={20} />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  const handleNext = () => {
    // Check if we're running low on questions (endless practice)
    if (currentIndex >= questions.length - 3 && !loadingMore) {
      // Pre-fetch more questions before running out
      loadMoreQuestions();
    }
    
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (loadingMore) {
      // Wait for more questions to load
      toast.info('Loading more questions...', { duration: 1500 });
    } else {
      // No more questions and nothing loading - show results or continue
      // In endless mode, we try to get more questions
      loadMoreQuestions();
    }
  };

  // Function to end practice session manually
  const handleEndPractice = () => {
    if (window.confirm('End this practice session?')) {
      setCurrentScreen('results');
    }
  };

  // Function to handle integer/numerical answer submission
  const handleIntegerSubmit = async () => {
    if (!integerAnswer.trim()) return;
    
    const currentQuestion = questions[currentIndex];
    const userAnswer = integerAnswer.trim();
    const correctAnswer = currentQuestion?.correct_answer?.toString().trim();
    
    // Save the time spent on this question
    setQuestionTimers(prev => ({
      ...prev,
      [currentIndex]: timerRef.current
    }));
    
    // Store the user's answer
    setUserAnswers(prev => ({
      ...prev,
      [currentIndex]: userAnswer
    }));
    
    // Compare answers (handle numeric comparison for flexibility)
    let isCorrectAnswer = false;
    try {
      // Try numeric comparison first (handles "3.0" == "3" cases)
      const userNum = parseFloat(userAnswer);
      const correctNum = parseFloat(correctAnswer);
      isCorrectAnswer = !isNaN(userNum) && !isNaN(correctNum) && Math.abs(userNum - correctNum) < 0.001;
    } catch {
      // Fall back to string comparison
      isCorrectAnswer = userAnswer.toLowerCase() === correctAnswer?.toLowerCase();
    }
    
    // If numeric comparison failed, try exact string match
    if (!isCorrectAnswer) {
      isCorrectAnswer = userAnswer === correctAnswer;
    }
    
    // Update stats
    if (isCorrectAnswer) {
      setStats(prev => ({
        ...prev,
        correct: prev.correct + 1,
        streak: prev.streak + 1
      }));
      toast.success('Correct');
    } else {
      setStats(prev => ({
        ...prev,
        wrong: prev.wrong + 1,
        streak: 0
      }));
      toast.error('❌ Wrong answer');
      // Save mistake to database
      try {
        await api.post('/mistakes/save', { question_id: currentQuestion.id });
      } catch (error) {
        logger.error('Failed to save mistake:', error);
      }
    }
    
    // Save response for analytics
    try {
      await api.post('/practice/save-response', {
        question_id: currentQuestion.id,
        selected_answer: userAnswer,
        is_correct: isCorrectAnswer,
        time_spent_seconds: timerRef.current
      });
    } catch (error) {
      logger.error('Failed to save practice response:', error);
    }
  };

  // Reset integer answer when moving to new question
  useEffect(() => {
    setIntegerAnswer('');
  }, [currentIndex]);

  // ===== RESULTS SCREEN =====
  const ResultsScreen = () => {
    const totalAttempted = stats.correct + stats.wrong;
    const accuracy = totalAttempted > 0 ? ((stats.correct / totalAttempted) * 100).toFixed(1) : 0;
    
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <h1 className="text-4xl font-bold text-foreground mb-2">Practice Complete</h1>
        <p className="text-lg text-muted-foreground mb-8">
          {selectedExam?.name} • {selectedSubject}
          {selectedChapterName && ` • ${selectedChapterName}`}
        </p>
        
        <Card className="p-8 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary">{totalAttempted}</div>
              <p className="text-sm text-muted-foreground">Questions Done</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600">{stats.correct}</div>
              <p className="text-sm text-muted-foreground">Correct</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600">{stats.wrong}</div>
              <p className="text-sm text-muted-foreground">Wrong</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">{accuracy}%</div>
              <p className="text-sm text-muted-foreground">Accuracy</p>
            </div>
          </div>
          
          <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl mb-6">
            <p className="text-lg font-semibold text-foreground">
              {accuracy >= 80 ? 'Outstanding — you know this topic well.' :
               accuracy >= 60 ? 'Good work. Keep practicing to improve further.' :
               accuracy >= 40 ? 'Nice effort. Review the explanations and try again.' :
               'Keep studying. Practice makes perfect.'}
            </p>
          </div>
          
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => {
                setStats({ correct: 0, wrong: 0, skipped: 0, streak: 0 });
                setUserAnswers({});
                setCurrentIndex(0);
                setShownQuestionIds([]);
                setCurrentScreen('subject');
              }}
              variant="outline"
              className="px-6"
            >
              Change Topic
            </Button>
            <Button
              onClick={() => {
                setStats({ correct: 0, wrong: 0, skipped: 0, streak: 0 });
                setUserAnswers({});
                setCurrentIndex(0);
                // Keep showing questions from where we left off
                setCurrentScreen('practice');
              }}
              className="px-6 bg-primary"
            >
              Continue Practicing
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  // ===== MAIN RENDER =====
  if (loading) {
    return (
      <Layout>
        <LoadingSpinner size="xl" text="Loading questions..." fullScreen={true} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {currentScreen === 'exam' && ExamSelectionScreen()}
        {currentScreen === 'mode' && ModeSelectionScreen()}
        {currentScreen === 'subject' && SubjectSelectionScreen()}
        {currentScreen === 'practice-type' && PracticeTypeScreen()}
        {currentScreen === 'chapter-selection' && ChapterSelectionScreen()}
        {currentScreen === 'practice' && PracticeScreen()}
        {currentScreen === 'results' && ResultsScreen()}
      </div>
    </Layout>
  );
};

export default PracticeNew;
